import { useCallback, useEffect, useRef, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import { extractErrorMessage, isSessionExpired } from '../../api/apiErrors';
import {
  cancelImportJob,
  createImportJob,
  fetchImportJob,
  type ImportJobItemInput,
  type ImportJobResponse,
  type ImportJobStatus,
} from '../../api/importJobs';
import type { IntegrationApi } from '../../connectionTypes';
import type {
  EntityKey,
  ImportRunItem,
  ImportWizardState,
  MappingPreviewItem,
  IntegrationSelectionState,
  WizardMode,
} from '../types';
import { mappingDisposition } from '../mappingReason';

type ApiContext = {
  baseUrl: string;
  nonce: string;
};

type UseImportJobParams = {
  api: ApiContext;
  mode: WizardMode;
  companyId: number | null;
  selectedConnectionIds: string[];
  selectedConnectionsById: Record<string, IntegrationApi>;
  onSessionExpired: () => void;
  getEntityKeysFor: (connectionId: string, remoteCompanyId: string) => EntityKey[];
  remoteCompanyLabelsByConnectionId: Record<string, Record<string, string>>;
  getState: () => ImportWizardState;
  setState: React.Dispatch<React.SetStateAction<ImportWizardState>>;
};

/**
 * Produces wizard run items from the final job poll response so downstream
 * steps (conflicts count, done step) can read per-company results without
 * needing a separate API call.
 *
 * Only `import` phase items are relevant — they carry the resolved
 * `target_company_id` and per-company success/failure.
 */
const buildRunItemsFromJob = (job: ImportJobResponse): ImportRunItem[] =>
  job.items
    .filter((item) => item.item_type === 'import' && item.target_company_id !== null)
    .map((item): ImportRunItem => ({
      id: `job-${job.job_id}-item-${item.id}`,
      connectionId: item.connection_id,
      connectionName: '',
      integrationSlug: item.provider_slug,
      remoteCompanyId: item.remote_company_id,
      remoteCompanyLabel: item.remote_label,
      entityKeys: [],
      targetCompanyId: item.target_company_id as number,
      includeFieldKeys: [],
      status: item.status === 'success' ? 'success' : 'failed',
      importSessionId: item.import_session_id,
      errorMessage: item.error?.message ?? null,
      ignoredFieldKeys: [],
    }));

/** Polling backoff: starts at 1 s, doubles each step, caps at 10 s. */
const nextPollMs = (currentMs: number): number => Math.min(currentMs * 2, 10_000);
const INITIAL_POLL_MS = 1_000;

const TERMINAL_STATUSES: ImportJobStatus[] = ['completed', 'partial', 'failed', 'cancelled'];

export type ImportJobState = {
  status: 'idle' | 'starting' | 'polling' | 'done' | 'error';
  jobId: number | null;
  job: ImportJobResponse | null;
  startError: string;
};

export const useImportJob = ({
  api,
  mode,
  companyId,
  selectedConnectionIds,
  selectedConnectionsById,
  onSessionExpired,
  getEntityKeysFor,
  remoteCompanyLabelsByConnectionId,
  getState,
  setState,
}: UseImportJobParams) => {
  const [jobState, setJobState] = useState<ImportJobState>({
    status: 'idle',
    jobId: null,
    job: null,
    startError: '',
  });

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef(INITIAL_POLL_MS);
  const abortedRef = useRef(false);

  const clearPollTimer = () => {
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollJob = useCallback(
    async (jobId: number) => {
      if (abortedRef.current) return;

      try {
        const job = await fetchImportJob(api, jobId);
        setJobState((prev) => ({ ...prev, job }));

        const isTerminal = TERMINAL_STATUSES.includes(job.status);

        if (isTerminal) {
          setJobState((prev) => ({ ...prev, status: 'done' }));
          setState((prev) => ({
            ...prev,
            run: {
              ...prev.run,
              status: job.status === 'completed' || job.status === 'partial' ? 'completed' : 'stopped',
              items: buildRunItemsFromJob(job),
            },
          }));
          return;
        }

        // Still running — schedule next poll with backoff, but reset on progress.
        const prevJob = jobState.job;
        const hasProgress =
          prevJob !== null &&
          (job.done_items !== prevJob.done_items || job.failed_items !== prevJob.failed_items);
        if (hasProgress) {
          pollIntervalRef.current = INITIAL_POLL_MS;
        } else {
          pollIntervalRef.current = nextPollMs(pollIntervalRef.current);
        }

        pollTimerRef.current = setTimeout(() => {
          void pollJob(jobId);
        }, pollIntervalRef.current);
      } catch (error) {
        if (isSessionExpired(error)) {
          onSessionExpired();
          return;
        }
        // On transient errors keep polling with backoff.
        pollIntervalRef.current = nextPollMs(pollIntervalRef.current);
        pollTimerRef.current = setTimeout(() => {
          void pollJob(jobId);
        }, pollIntervalRef.current);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, onSessionExpired, setState],
  );

  const buildJobItems = useCallback((): ImportJobItemInput[] => {
    const state = getState();
    const items: ImportJobItemInput[] = [];

    for (const connectionId of selectedConnectionIds) {
      const connection = selectedConnectionsById[connectionId];
      if (!connection) continue;

      const selection: IntegrationSelectionState | undefined = state.selectionsByConnectionId[connectionId];
      if (!selection) continue;

      const mappingItems: MappingPreviewItem[] = state.mapping.itemsByConnectionId[connectionId] ?? [];
      const mappingByRemoteId = Object.fromEntries(
        mappingItems.map((m) => [m.remote_company_id, m.target_company_id]),
      );
      const blockedRemoteIds = new Set(
        mappingItems
          .filter((m) => mappingDisposition(m.reason) === 'blocked')
          .map((m) => m.remote_company_id),
      );

      for (const remoteCompanyId of selection.selectedRemoteCompanyIds) {
        if (blockedRemoteIds.has(remoteCompanyId)) continue;
        const attachId = state.attachTargetByRemoteId[connectionId]?.[remoteCompanyId] ?? 0;
        const allowCreate = state.allowCreateByRemoteId[connectionId]?.[remoteCompanyId] ?? false;
        const fromMapping = mappingByRemoteId[remoteCompanyId];

        const targetCompanyId: number =
          mode === 'single' && companyId !== null
            ? companyId
            : (fromMapping ?? attachId) > 0
              ? (fromMapping ?? attachId)!
              : 0;

        const remoteLabel = remoteCompanyLabelsByConnectionId[connectionId]?.[remoteCompanyId] ?? '';
        const entityKeys = getEntityKeysFor(connectionId, remoteCompanyId);

        const item: ImportJobItemInput = {
          connection_id: connectionId,
          provider_slug: connection.restBasePath,
          remote_company_id: remoteCompanyId,
          remote_label: remoteLabel,
          entity_keys: entityKeys,
          allow_create: allowCreate || targetCompanyId === 0,
        };

        if (targetCompanyId > 0) {
          item.target_company_id = targetCompanyId;
        }

        if (selection.selectedFieldIds.length > 0) {
          item.include_field_keys = selection.selectedFieldIds.slice();
        }

        items.push(item);
      }
    }

    return items;
  }, [
    companyId,
    getEntityKeysFor,
    getState,
    mode,
    remoteCompanyLabelsByConnectionId,
    selectedConnectionIds,
    selectedConnectionsById,
  ]);

  const startJob = useCallback(async () => {
    abortedRef.current = false;
    clearPollTimer();
    pollIntervalRef.current = INITIAL_POLL_MS;

    setJobState({ status: 'starting', jobId: null, job: null, startError: '' });
    setState((prev) => ({
      ...prev,
      run: { ...prev.run, status: 'running', stopRequested: false },
      conflicts: { status: 'idle', items: [] },
    }));

    try {
      const jobItems = buildJobItems();
      if (jobItems.length === 0) {
        setJobState({ status: 'done', jobId: null, job: null, startError: '' });
        setState((prev) => ({ ...prev, run: { ...prev.run, status: 'completed' } }));
        return;
      }

      const { job_id } = await createImportJob(api, jobItems);
      setJobState((prev) => ({ ...prev, status: 'polling', jobId: job_id }));
      void pollJob(job_id);
    } catch (error) {
      if (isSessionExpired(error)) {
        onSessionExpired();
        return;
      }
      const msg = extractErrorMessage(error, __('Démarrage import impossible.'));
      setJobState({ status: 'error', jobId: null, job: null, startError: msg });
      setState((prev) => ({ ...prev, run: { ...prev.run, status: 'idle' } }));
    }
  }, [api, buildJobItems, onSessionExpired, pollJob, setState]);

  const stopJob = useCallback(async () => {
    abortedRef.current = true;
    clearPollTimer();

    const { jobId } = jobState;
    if (jobId === null) return;

    try {
      await cancelImportJob(api, jobId);
    } catch {
      // best-effort
    }

    setJobState((prev) => ({ ...prev, status: 'done' }));
    setState((prev) => ({ ...prev, run: { ...prev.run, status: 'stopped' } }));
  }, [api, jobState, setState]);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      abortedRef.current = true;
      clearPollTimer();
    },
    [],
  );

  return {
    jobState,
    startJob,
    stopJob,
  };
};
